import type { SkinGachaRollResponse } from '@kenocyber/shared';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { socketCapacityLabel } from './SkinWardrobe';
import {
  chargeMoteCount,
  type SkinRevealPhase,
  showsSkin,
  showsSockets,
  skinRevealTimeline,
  socketPipDelayMs,
} from './skin-gacha-reveal-timeline';

/**
 * The reveal: energy, an opening, a skin and its seats (gate V24C).
 *
 * ## It presents; it never decides
 *
 * The copy was drawn by the server's own entropy, charged, granted and
 * persisted before this component existed. Skipping moves a phase. Closing
 * unmounts a div. Neither calls the server, re-rolls anything or returns a
 * coin, and the suite asserts that rather than trusting the sentence.
 *
 * ## No roulette that "almost stopped" somewhere else
 *
 * There is no strip of prizes sliding past, no deceleration and no near miss.
 * The outcome is already true when this mounts, and animating a machine that
 * pretends to be choosing would be dramatising a decision that is over. What
 * plays instead is a containment field opening onto the thing that is already
 * inside it.
 *
 * ## The three skins get the same show
 *
 * Same duration, same field, same colours, same everything. Nothing here reads
 * the definition id for anything except the picture and the name. The weights
 * are equal - exactly one in three - and no skin has a rarity, so a reveal that
 * treated one as a jackpot would be teaching a hierarchy the game denies.
 *
 * ## The capacity is shown, not celebrated
 *
 * One to six seats, drawn as pips, at the same brightness whatever the count.
 * Six is rarer than one; it is not stronger, because the seats are empty and
 * jewels do not exist. The socket phase's length does not depend on the number.
 *
 * ## No canvas, no renderer, no library
 *
 * Absolutely positioned spans animated on `transform` and `opacity` - the two
 * properties a compositor can run without touching layout - plus the same PNG
 * the wardrobe draws, at its own colours, with no tint and no filter over it.
 * One WebGL context belongs to the arena and a reveal must never open a second.
 */

interface SkinGachaRevealProps {
  /**
   * Resolves the skin's PNG, or answers null.
   *
   * Passed in rather than looked up here so the reveal has no I/O of its own:
   * the sidecar is fetched once per page by the panel above, and this component
   * stays a pure function of a result and a resolver.
   */
  readonly artFor: (definitionId: string) => string | null;
  readonly result: SkinGachaRollResponse;
  /** Told when the sequence has finished playing. Never called by a failure. */
  readonly onFinished: () => void;
  /** The player closed it. The copy is already theirs either way. */
  readonly onClose: () => void;
  /** Opens the wardrobe on the copy. Looking, never equipping. */
  readonly onOpenWardrobe: () => void;
}

/** Stable names for the drifting field. Never an array index as a key. */
const MOTE_SLOTS = Array.from({ length: 24 }, (_, index) => `mote-${index}`);

/** Stable names for the seats, up to the six that can exist. */
const SEAT_SLOTS = ['seat-1', 'seat-2', 'seat-3', 'seat-4', 'seat-5', 'seat-6'] as const;

/**
 * Whether the tab is on screen right now.
 *
 * A hidden tab gets no visual work: the field is unmounted and the root carries
 * `data-hidden`, which pauses every animation in the stylesheet. Browsers
 * throttle background animation already, and "already" is not "guaranteed".
 */
function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(
    () => typeof document === 'undefined' || document.visibilityState === 'visible',
  );
  useEffect(() => {
    const follow = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', follow);
    return () => document.removeEventListener('visibilitychange', follow);
  }, []);
  return visible;
}

export function SkinGachaReveal({
  artFor,
  result,
  onFinished,
  onClose,
  onOpenWardrobe,
}: SkinGachaRevealProps) {
  const reducedMotion = useMemo(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );
  const visible = useDocumentVisible();
  const timeline = useMemo(() => skinRevealTimeline({ reducedMotion }), [reducedMotion]);
  const [phase, setPhase] = useState<SkinRevealPhase>(() => timeline[0]?.phase ?? 'done');
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const dialog = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const summaryId = useId();

  /** Cancels everything pending. Stable, so the effects can depend on it. */
  const clearTimers = useCallback(() => {
    for (const timer of timers.current) {
      clearTimeout(timer);
    }
    timers.current = [];
  }, []);

  /**
   * Plays the sequence once, then settles.
   *
   * `onFinished` travels through a ref rather than the dependency list: it is
   * an inline arrow in the caller and the arena re-renders several times a
   * second, so naming it here would tear the chain down and restart it faster
   * than its own timers could ever fire - which is exactly the bug the store
   * panel documents.
   */
  const finished = useRef(onFinished);
  finished.current = onFinished;

  useEffect(() => {
    let elapsed = 0;
    for (const step of timeline) {
      timers.current.push(setTimeout(() => setPhase(step.phase), elapsed));
      elapsed += step.durationMs;
    }
    timers.current.push(
      setTimeout(() => {
        setPhase('done');
        finished.current();
      }, elapsed),
    );
    // The cleanup cancels the WHOLE chain, so a skip or an unmount mid-sequence
    // cannot leave a `setTimeout` firing into a tree that no longer exists.
    return clearTimers;
  }, [timeline, clearTimers]);

  /**
   * Ends the sequence now, with everything on screen.
   *
   * It does not touch `result`, which came out of a committed transaction, and
   * it does not close: a player must never be able to skip their own copy away
   * before seeing what they bought.
   */
  const skip = useCallback(() => {
    clearTimers();
    setPhase('done');
    finished.current();
  }, [clearTimers]);

  /** Focus lands in the dialog on mount. The caller returns it on close. */
  useEffect(() => {
    dialog.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        // Escape during the build SKIPS rather than hides. Once everything is
        // on screen it closes, which is the ordinary meaning.
        if (phase === 'done') {
          onClose();
        } else {
          skip();
        }
        return;
      }
      if (event.key !== 'Tab' || !dialog.current) {
        return;
      }
      /*
       * A modal keeps the keyboard.
       *
       * Without this, Tab walks out of the dialog and into the inventory behind
       * it - which is invisible to a sighted player and completely lost to
       * somebody using a screen reader. The trap is deliberately small: the
       * focusable elements are this component's own buttons.
       */
      const focusable = [...dialog.current.querySelectorAll<HTMLElement>('button:not([disabled])')];
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === dialog.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose, phase, skip]);

  const art = artFor(result.definitionId);
  const seats = SEAT_SLOTS.slice(0, result.socketCapacity);
  const motes = MOTE_SLOTS.slice(0, chargeMoteCount({ reducedMotion }));
  const revealed = showsSkin(phase);
  const seatsVisible = showsSockets(phase);

  /**
   * The sentence a screen reader announces once everything has landed.
   *
   * Announced on `done` rather than per phase: a live region that changed four
   * times in two seconds would read the same result four times over its own
   * previous announcement.
   */
  const summary =
    phase === 'done'
      ? `${result.displayName}. ${socketCapacityLabel(result.socketCapacity)}. A cópia foi guardada no vestiário e não foi equipada.`
      : '';

  return createPortal(
    <div
      className="skin-reveal-backdrop"
      data-testid="skin-reveal"
      /*
       * "This portal belongs to the inventory."
       *
       * The panel closes itself on any `mousedown` outside its own node, and it
       * listens on `document` in the capture phase - so it sees the raw DOM
       * target, and a portal into `document.body` looks exactly like the
       * outside world. Without this marker, clicking `PULAR ANIMAÇÃO` closed
       * the inventory out from under the player's own result.
       */
      data-inventory-portal="skin-gacha-reveal"
      data-phase={phase}
      data-reduced={reducedMotion ? 'true' : 'false'}
      // Every animation in the stylesheet is paused off this attribute, so a
      // backgrounded tab does no visual work at all.
      data-hidden={visible ? 'false' : 'true'}
    >
      {/*
        `tabIndex={-1}` on a `role="dialog"` is what makes the container
        programmatically focusable without putting it in the tab order. Focus
        lands here on mount, so the keyboard starts inside the modal rather than
        behind it, and the Tab trap below keeps it there.
      */}
      <div
        className="skin-reveal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={summaryId}
        ref={dialog}
        tabIndex={-1}
      >
        <div className="skin-reveal-stage" data-testid="skin-reveal-stage">
          {/* The field. Unmounted when the tab is hidden and under reduced
              motion - not merely stopped, so nothing is left holding layers. */}
          {visible && motes.length > 0 ? (
            <span className="skin-reveal-motes" data-testid="skin-reveal-motes" aria-hidden="true">
              {motes.map((slot, index) => (
                <span
                  key={slot}
                  className="skin-reveal-mote"
                  style={
                    {
                      '--mote-angle': `${index * 15}deg`,
                      '--mote-delay': `${(index % 6) * 90}ms`,
                    } as React.CSSProperties
                  }
                />
              ))}
            </span>
          ) : null}
          <span className="skin-reveal-core" aria-hidden="true" />
          <span className="skin-reveal-ring" data-ring="0" aria-hidden="true" />
          <span className="skin-reveal-ring" data-ring="1" aria-hidden="true" />

          {/*
            The picture, at its own colours.
            No tint, no filter, no crop and no re-encode: the PNG the pipeline
            produced is the PNG a player sees, which is the rule the whole skin
            content chain rests on.
          */}
          <span className="skin-reveal-art">
            {revealed && art ? (
              <img
                src={art}
                alt=""
                draggable={false}
                decoding="async"
                data-testid="skin-reveal-art"
              />
            ) : (
              <span className="skin-reveal-art-hole" aria-hidden="true" />
            )}
          </span>
        </div>

        {/*
          Fixed height, always.
          The name and the seats arrive in the middle of the sequence, and a
          caption that grew when they did would push the stage off its own
          centre - a layout jump precisely when the player is looking.
        */}
        <div className="skin-reveal-caption">
          {revealed ? (
            <h3 className="skin-reveal-name" id={titleId} data-testid="skin-reveal-name">
              {result.displayName}
            </h3>
          ) : (
            <span className="skin-reveal-name-placeholder" id={titleId} aria-hidden="true" />
          )}
          <p className="skin-reveal-capacity" data-testid="skin-reveal-capacity">
            <span className="skin-sockets" aria-hidden="true">
              {seats.map((seat, index) => (
                <span
                  key={seat}
                  className="skin-socket skin-socket-empty skin-reveal-seat"
                  data-testid="skin-reveal-seat"
                  data-visible={seatsVisible ? 'true' : 'false'}
                  style={
                    {
                      '--seat-delay': `${socketPipDelayMs(index, { reducedMotion })}ms`,
                    } as React.CSSProperties
                  }
                />
              ))}
            </span>
            <span className="skin-reveal-capacity-label">
              {seatsVisible ? socketCapacityLabel(result.socketCapacity) : ' '}
            </span>
          </p>
        </div>

        {/*
          One live region, read once.
          `aria-live` rather than `role="alert"`: this is information, not an
          interruption, and polite is what lets it finish the sentence a player
          was already hearing.
        */}
        <p
          className="skin-reveal-summary"
          id={summaryId}
          aria-live="polite"
          data-testid="skin-reveal-summary"
        >
          {summary}
        </p>

        <div className="skin-reveal-actions">
          {phase === 'done' ? (
            <>
              <button
                type="button"
                className="skin-button"
                data-testid="skin-reveal-wardrobe"
                onClick={onOpenWardrobe}
              >
                VER NO VESTIÁRIO
              </button>
              <button
                type="button"
                className="skin-button skin-button-primary"
                data-testid="skin-reveal-close"
                onClick={onClose}
              >
                FECHAR
              </button>
            </>
          ) : (
            <button
              type="button"
              className="skin-button"
              data-testid="skin-reveal-skip"
              // Enter and Space come free on a real <button>; nothing here
              // reimplements them badly.
              onClick={skip}
            >
              PULAR ANIMAÇÃO
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
